import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Users, Calendar, MapPin, Hash } from "lucide-react";
import { TEAMS, SCHEDULE } from "../data/tournament";
import { api } from "../lib/api";

const POSITIONS = {
  G: { label: "Gardien", color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
  D: { label: "Défenseur", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  A: { label: "Attaquant", color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const TeamDetail = () => {
  const { id } = useParams();
  const team = TEAMS.find((t) => t.id === id);
  const [serverTeam, setServerTeam] = useState(null);
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    if (!team) return;
    api.get(`/teams/${id}`).then((r) => setServerTeam(r.data)).catch(() => setServerTeam(null));
    api.get(`/teams/${id}/roster`).then((r) => setRoster(r.data)).catch(() => setRoster([]));
  }, [id, team]);

  const games = useMemo(() => {
    if (!team) return [];
    return SCHEDULE.filter((g) => g.away === team.name || g.home === team.name);
  }, [team]);

  if (!team) return <Navigate to="/equipes" replace />;

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="team-detail-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <Link
            to="/equipes"
            data-testid="team-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#E60012] font-condensed uppercase tracking-[0.2em] text-xs mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Toutes les équipes
          </Link>

          <div className="flex flex-col md:flex-row items-start md:items-end gap-8">
            <div className="w-32 h-32 sm:w-40 sm:h-40 bg-black border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
              {serverTeam?.logo_base64 ? (
                <img src={serverTeam.logo_base64} alt={team.name} className="w-full h-full object-contain p-2" />
              ) : (
                <Shield className="w-16 h-16 text-[#E60012]/70" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1">
              <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
                Catégorie {team.category} · M9
              </span>
              <h1 className="mt-2 font-display text-5xl sm:text-7xl uppercase leading-[0.9]">
                {team.name}
              </h1>
              {serverTeam?.motto && (
                <p className="mt-3 text-white/70 italic text-lg">« {serverTeam.motto} »</p>
              )}
              {serverTeam?.description && (
                <p className="mt-4 text-white/60 max-w-2xl text-sm leading-relaxed">{serverTeam.description}</p>
              )}
              <div className="mt-5 flex flex-wrap gap-3 text-xs font-condensed uppercase tracking-[0.2em] text-white/60">
                <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-[#E60012]" /> {roster.length} joueur{roster.length > 1 ? "s" : ""}</span>
                <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#E60012]" /> {games.length} match{games.length > 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roster */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-center gap-3 mb-8">
          <Users className="w-5 h-5 text-[#E60012]" />
          <h2 className="font-display text-3xl sm:text-4xl uppercase">Alignement</h2>
        </div>

        {roster.length === 0 ? (
          <div className="bg-[#111] border border-white/10 p-10 text-center">
            <Shield className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
            <div className="font-display text-2xl uppercase">Alignement à venir</div>
            <p className="mt-2 text-white/55 text-sm">Le roster sera publié sous peu.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {roster.map((p, i) => {
              const pos = POSITIONS[p.position] || { label: "—", color: "bg-white/10 text-white/60 border-white/15" };
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-[#111] border border-white/10 hover:border-[#E60012] transition-all overflow-hidden"
                  data-testid={`player-${p.id}`}
                >
                  <div className="aspect-square bg-black/50 relative overflow-hidden">
                    {p.photo_base64 ? (
                      <img src={p.photo_base64} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Shield className="w-12 h-12 text-[#E60012]/40" strokeWidth={1.2} />
                      </div>
                    )}
                    {p.number && (
                      <div className="absolute top-2 left-2 bg-[#E60012] text-white px-2 py-1 font-display text-2xl leading-none">
                        #{p.number}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-condensed uppercase tracking-[0.05em] text-sm truncate">{p.name}</div>
                    {p.position && (
                      <span className={`mt-1.5 inline-block px-2 py-0.5 text-[10px] font-condensed uppercase tracking-wider border ${pos.color}`}>
                        {pos.label}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Schedule for this team */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-5 h-5 text-[#E60012]" />
          <h2 className="font-display text-3xl sm:text-4xl uppercase">Calendrier</h2>
        </div>
        <div className="space-y-2">
          {games.map((g) => {
            const isAway = g.away === team.name;
            const opponent = isAway ? g.home : g.away;
            return (
              <Link
                key={g.num}
                to="/horaire"
                className="flex flex-wrap items-center gap-3 px-4 py-3 bg-[#111] border border-white/10 hover:border-[#E60012] transition-colors text-sm"
                data-testid={`team-game-${g.num}`}
              >
                <span className="font-display text-xl text-[#E60012] w-8">#{g.num}</span>
                <span className="font-condensed text-white/80 w-28">{g.day}</span>
                <span className="font-condensed text-white">{g.time}</span>
                <span className="text-white/60 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.ice}</span>
                <span className="ml-auto flex items-center gap-2">
                  <span className="text-white/50 text-xs uppercase tracking-wider font-condensed">VS</span>
                  <span className="font-condensed text-white">{opponent}</span>
                </span>
                {g.phase && <span className="text-[#E60012] text-xs font-condensed uppercase tracking-wider">{g.phase}</span>}
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default TeamDetail;
